<script setup lang="ts">
import { FieldStyle } from "@/builder/enums";
import { useRenderStore } from '@/builder/stores/renderStore';
import { Ref, computed, inject, onBeforeMount, onMounted, ref, watch } from 'vue';
import { PageDesign } from '../page/PageInterface';

const renderStore = useRenderStore();
const pageDesign = inject("pageDesign") as Ref<PageDesign>;
const fieldStyle = computed(() => pageDesign.value.fieldStyle);

const props = defineProps([
    'id',
    'value',
    'label',
    'isHeading',
    'accessibleLabel',
    'labelFor',
    'labelClasses',
    'isRequired',
    'isEmail',
    'name',
    'maxLength',
    'isInvalid',
    'describedBy',
    'displayName',
    'isStepError'
]);

// refs
const error = ref<string | null>(null);
const timeout = ref<any>(null);

// computed
const getAriaDescribedBy = computed(() => error.value ? `errorMessage_` + getHtmlId.value : null);
const getAriaIsInvalid = computed(() => error.value ? true : false);
const getHtmlId = computed(() => props.id + '_' + props.name);
const getLabelText = computed(() => props.label ? props.label : props.accessibleLabel);

// methods
const validate = (val: string): void => {
    if (props.isRequired && !props.value) {
        const inputName = props.displayName ? props.displayName : props.label;
        if (inputName.length > 0) {
            error.value = `${inputName} is required.`;
        } else {
            error.value = "This field is required.";
        }
    } else if (props.isEmail) {
        const isEmailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!isEmailRegex.test(val)) {
            error.value = "Please enter a valid email address.";
        } else {
            error.value = null;
        }
    } else if (props.maxLength && props.value && props.value.length > props.maxLength) {
        error.value = `Please enter no more than ${props.maxLength} characters.`;
    } else {
        error.value = null;
    }
};

const setModelState = (modelState): void => {
    const errorMsg = modelState[props.name];
    if (errorMsg) {
        error.value = Array.isArray(errorMsg) ? errorMsg[0] : errorMsg;
    }

    const errorLower = modelState[props.name.toLowerCase()];
    if (errorLower) {
        error.value = Array.isArray(errorLower) ? errorLower[0] : errorLower;
    }
};

const setError = (err: string | null): void => {
    error.value = err;
};

// expose
defineExpose({ setError, validate });

// lifecycle hooks
onMounted(() => {
    setModelState(renderStore.modelState);
});

onBeforeMount(() => {
    if (error.value) {
        const index = renderStore.errors.findIndex(x => x.propertyId === getHtmlId.value);
        if (index > -1) renderStore.errors.splice(index, 1);
    }
});

// watch
watch(() => props.value, (val) => {
    if (error.value) {
        error.value = null;
    }

    if (timeout.value) {
        clearTimeout(timeout.value);
    }

    timeout.value = setTimeout(() => {
        validate(val);
    }, 500);
});

watch(error, (newValue, oldValue) => {
    if (oldValue) {
        const index = renderStore.errors.findIndex(x => x.propertyId === getHtmlId.value);
        if (index > -1) renderStore.errors.splice(index, 1);
    }
    if (newValue) {
        const formStep = renderStore.getFormStep(props.id);
        if (formStep !== null) {
            renderStore.errors.push({ componentId: props.id, propertyId: getHtmlId.value, error: newValue, step: formStep });
        }
    }
});

watch(() => renderStore.modelState, (modelState) => {
    setModelState(modelState);
});

watch(() => renderStore.validateInputStep, (validateInputStep) => {
    if (validateInputStep === null) { return; }
    const parentStep = renderStore.getFormStep(props.id);
    if (parentStep != null && validateInputStep === parentStep) {
        validate(props.value);
    }
});

watch(() => renderStore.validating, (validating) => {
    if (validating) {
        validate(props.value);
    }
});
</script>

<template>
    <template v-if="isStepError">
        <slot name="input" :describedBy="getAriaDescribedBy"></slot>

        <span v-if="error" :id="`errorMessage_` + getHtmlId" class="input-msg input-msg-error">
            {{ error }}
        </span>
    </template>

    <div v-else class="form-field-container" :class="[{ 'has-error': error },
    { 'form-underline': fieldStyle === FieldStyle.Underline },
    { 'form-compact': fieldStyle === FieldStyle.Compact }]">
        <template v-if="isHeading">
            <span class="label" :class="labelClasses">
                {{ getLabelText }}<span v-if="isRequired" class="req" aria-hidden="true">*</span>
            </span>
        </template>

        <template v-else>
            <label :for="labelFor ? labelFor : getHtmlId" :class="[{ 'rd-sr-only': !label }, labelClasses]">
                {{ getLabelText }}<span v-if="isRequired" class="req" aria-hidden="true">*</span>
            </label>
        </template>

        <div v-if="fieldStyle == FieldStyle.Underline" class="rd-relative">
            <slot name="input" :describedBy="getAriaDescribedBy" :required="isRequired" :isInvalid="getAriaIsInvalid">
            </slot>

            <span class="border-input-focus"></span>
        </div>

        <slot v-else name="input" :describedBy="getAriaDescribedBy" :required="isRequired"
            :isInvalid="getAriaIsInvalid">
        </slot>

        <slot name="extraContent"></slot>

        <span v-if="error" :id="`errorMessage_` + getHtmlId" class="input-msg input-msg-error">
            {{ error }}
        </span>
    </div>
</template>
